import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OrderListItem } from "@/api/models/api-types";
import { formatPrice } from "../utils/format-price";
import { cn } from "@/lib/utils";
import { getOrderGroupStatusConfig } from "../utils/get-status-config";

interface OrderCardProps {
  order: OrderListItem;
}

export const OrderCard: React.FC<OrderCardProps> = ({ order }) => {
  const { t, i18n } = useTranslation();

  // Format date
  const orderDate = new Date(order.createdAt).toLocaleDateString(i18n.resolvedLanguage, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const statusBadge = getOrderGroupStatusConfig(String(order.status), t);

  // Calculate delivery and product counts
  const orderCount = order.orderCount || 0; // Default, can be calculated from order data if available
  const productCount = order.productCount || 0;

  // Get recipient name from order data
  const recipientName = order.recipientName || "-";

  // Handle images array - show first 3, then +X if more
  const orderImages = order.images || [];
  const displayedImages = orderImages.slice(0, 3);
  const remainingImagesCount =
    orderImages.length > 3 ? orderImages.length - 3 : 0;

  return (
    <Link
      to={`/orders/${order.orderGroupId}`}
      className="block border border-border-secondary dark:border-gray-600 rounded-xl p-2.5 md:p-4 cursor-pointer hover:border-indigo-400 hover:shadow-sm dark:hover:border-indigo-500 transition-all"
    >
      {/* Mobile Layout */}
      <div className="flex flex-col md:hidden gap-2.5">
        {/* Date and Status */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <p className="text-xs font-medium text-text-primary dark:text-text-primary">
            {orderDate}
          </p>
          <div
            className={cn(
              "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md",
              statusBadge.bg,
              statusBadge.text,
            )}
          >
            {statusBadge.icon && (
              <span className="w-3 h-3">{statusBadge.icon}</span>
            )}
            <span className="text-[10px] font-medium leading-tight">
              {statusBadge.label}
            </span>
          </div>
        </div>

        {/* Product Images - Mobile (moved up) */}
        {displayedImages.length > 0 && (
          <div className="flex gap-1">
            {displayedImages.map((image, index) => (
              <div
                key={index}
                className="relative w-12 h-12 rounded-md overflow-hidden bg-bg-tertiary dark:bg-gray-700 flex-shrink-0"
              >
                <img
                  src={image}
                  alt={`Order item ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                {/* Remaining images count badge on last image */}
                {index === displayedImages.length - 1 &&
                  remainingImagesCount > 0 && (
                    <div className="absolute top-0 right-0 bg-black/70 text-white text-[9px] font-semibold px-1 py-0.5 rounded-bl-md backdrop-blur-sm">
                      +{remainingImagesCount}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}

        {/* Order Details - Mobile Grid */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          <div className="flex flex-col gap-0.5">
            <p className="text-[10px] text-text-tertiary dark:text-gray-400">
              {t("orders.card.items")}
            </p>
            <p className="text-[11px] font-medium text-text-secondary dark:text-gray-300">
              {orderCount} {t("orders.card.brand")}, {productCount}{" "}
              {t("orders.card.products")}
            </p>
          </div>
          <div className="flex flex-col gap-0.5">
            <p className="text-[10px] text-text-tertiary dark:text-gray-400">
              {t("orders.card.total")}
            </p>
            <p className="text-[11px] font-medium text-text-secondary dark:text-gray-300">
              {formatPrice(order.totalAmount, order.currency)}
            </p>
          </div>
          <div className="flex flex-col gap-0.5">
            <p className="text-[10px] text-text-tertiary dark:text-gray-400">
              {t("orders.card.orderNumber")}
            </p>
            <p className="text-[11px] font-medium text-text-secondary dark:text-gray-300 truncate">
              {order.orderGroupId}
            </p>
          </div>
          <div className="flex flex-col gap-0.5">
            <p className="text-[10px] text-text-tertiary dark:text-gray-400">
              {t("orders.card.recipient")}
            </p>
            <p className="text-[11px] font-medium text-text-secondary dark:text-gray-300 truncate">
              {recipientName}
            </p>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex gap-10 items-center">
        {/* Left Section - Order Info */}
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          {/* Date and Status */}
          <div className="flex gap-3 items-center flex-wrap">
            <p className="text-base font-medium text-text-primary dark:text-text-primary">
              {orderDate}
            </p>
            <div
              className={cn(
                "inline-flex items-center gap-1 px-2 py-1 rounded-md",
                statusBadge.bg,
                statusBadge.text,
              )}
            >
              {statusBadge.icon && (
                <span className="w-4 h-4">{statusBadge.icon}</span>
              )}
              <span className="text-sm font-medium">{statusBadge.label}</span>
            </div>
          </div>

          {/* Order Details */}
          <div className="flex gap-14 items-end flex-wrap">
            <div className="flex flex-col gap-0.5">
              <p className="text-xs text-text-tertiary dark:text-gray-400">
                {t("orders.card.items")}
              </p>
              <p className="text-sm font-medium text-text-secondary dark:text-gray-300">
                {orderCount} {t("orders.card.brand")}, {productCount}{" "}
                {t("orders.card.products")}
              </p>
            </div>
            <div className="flex flex-col gap-0.5 min-w-0">
              <p className="text-xs text-text-tertiary dark:text-gray-400">
                {t("orders.card.orderNumber")}
              </p>
              <p className="text-sm font-medium text-text-secondary dark:text-gray-300 truncate">
                {order.orderGroupId}
              </p>
            </div>
            <div className="flex flex-col gap-0.5">
              <p className="text-xs text-text-tertiary dark:text-gray-400">
                {t("orders.card.total")}
              </p>
              <p className="text-sm font-medium text-text-secondary dark:text-gray-300">
                {formatPrice(order.totalAmount, order.currency)}
              </p>
            </div>
            <div className="flex flex-col gap-0.5 min-w-0">
              <p className="text-xs text-text-tertiary dark:text-gray-400">
                {t("orders.card.recipient")}
              </p>
              <p className="text-sm font-medium text-text-secondary dark:text-gray-300 truncate">
                {recipientName}
              </p>
            </div>
          </div>
        </div>

        {/* Right Section - Product Images */}
        {displayedImages.length > 0 && (
          <div className="flex gap-1.5 flex-shrink-0">
            {displayedImages.map((image, index) => (
              <div
                key={index}
                className="relative w-[72px] h-[72px] rounded-lg overflow-hidden bg-bg-tertiary dark:bg-gray-700"
              >
                <img
                  src={image}
                  alt={`Order item ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                {/* Remaining images count badge on last image */}
                {index === displayedImages.length - 1 &&
                  remainingImagesCount > 0 && (
                    <div className="absolute top-0 right-0 bg-black/70 text-white text-xs font-semibold px-1.5 py-0.5 rounded-bl-lg">
                      +{remainingImagesCount}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
};
